import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { isJSONString } from "utils";
import {
  ContentCopy,
  Edit,
  Reply,
  Download,
  Info,
  PushPinOutlined,
  NotificationsNone,
  NotificationsOffOutlined,
  History,
} from "@mui/icons-material";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import { ActionMenu, ConfirmDialog } from "components";
import { ConversationActions, NotificationSettingActions } from "redux-store";
import { StorageUtil } from "utils";
import { saveMessageInQueue, useConversationContext } from "../ConversationContext";
import MessageDetailDrawer from "./MessageDetailDrawer";
import { getInteractor } from "services/local.service";
import { useCleanUpEffect, useDownloadFile } from "hooks";
import { getMessageFileList, replaceId2Name } from "utils/message.utils";
import { DeleteAllIcon, DeleteIcon, UnpinIcon } from "components/icons";
import { useAlertContext } from "components/context/AlertContext";
import { getSavedServer } from "utils/branch.utils";
import { convertTimeLabel } from "../../GroupInfoDrawer/GroupSettingMessage/setting-message.helper";
import { useEditingHistoryContext } from "../context/EditingHistoryContext";

const ChatActionMenu = ({ message, menuAnchor, isThreadMode, onEditMessage, onClose, isThreadSetting, isSendErr }) => {
  const dispatch = useDispatch();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);
  const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID);
  const isMine = message.senderId === accountId;
  const isCurrentDevice = message.senderDeviceId === deviceId;
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const { downloadFile } = useDownloadFile();
  const { isMounted } = useCleanUpEffect();
  const { showAlert } = useAlertContext();
  const { groupDetail } = useConversationContext();
  const { showHistory } = useEditingHistoryContext();

  const serverOptions = getSavedServer().serverOptions;
  const MAX_GROUP_MESSAGE_PIN = serverOptions.maxGroupMessagePin;
  const editMessageTime = serverOptions.editMessageTime;
  const deleteMessageTime = serverOptions.deleteMessageTime;
  const isAllowEdit = isCurrentDevice
    ? Date.now() - message.modified < editMessageTime
    : Date.now() - message.created < editMessageTime;
  const isAllowDeleteAll = isCurrentDevice
    ? false === Boolean(message.modified) || Date.now() - message.modified < deleteMessageTime
    : Date.now() - message.created < deleteMessageTime;

  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  const [isDetailMessage, setIsDetailMessage] = useState(false);
  const [isPin, setIsPin] = useState(false);
  const [isShowConfirmUnpin, setIsShowConfirmUnpin] = useState(false);
  const [isTurnOffThreadNotice, setIsTurnOffThreadNotice] = useState(false);
  const [isShowConfirmDeleteOnlyMe, setIsShowConfirmDeleteOnlyMe] = useState(false);

  const handleReplyMessage = () => {
    dispatch(
      ConversationActions.setSelectGroupId({
        selectedGroupId: message.groupId,
        threadingId: Boolean(message.threadId) ? message.threadId : message.sourceId,
      }),
    );
    onClose();
  };

  const handleDownLoadFile = async () => {
    if (MEDIA_TYPES.includes(message.sendType) && isJSONString(message.content)) {
      const msgFileList = getMessageFileList(message);
      downloadFile(msgFileList, message.groupId);
      onClose();
    } else {
      // Show alert if app can not download file
      showAlert({
        content: getNSLang(LangConstant.NS_HOME_CONVERSATION, LangConstant.TXT_FILE_NOT_FOUND),
        alertProps: { severity: "error" },
      });
    }
  };

  const handleCopyContent = async () => {
    const groupDetail = (await getInteractor(prefixKey).LocalGroupService.get(message.groupId)) || {};
    const groupMembers = groupDetail.groupMembers || [];
    navigator?.clipboard.writeText(await replaceId2Name(message.content, groupMembers, false));
    onClose();
  };

  const handleShowConfirmDialog = () => {
    if (isAllowDeleteAll) {
      setIsOpenConfirmDialog(true);
    } else {
      showAlert({
        content: getNSLang(LangConstant.NS_HOME_CONVERSATION, "FM_TOAST_DELETE_ALL", {
          time: convertTimeLabel(deleteMessageTime),
        }),
        severity: "info",
      });
    }
    onClose();
  };

  const handleCloseConfirmDialog = () => {
    if (isMounted()) setIsOpenConfirmDialog(false);
    onClose();
  };

  const handleDeleteMessage = async () => {
    const options = {};
    if (message.sendAsDirectMessage)
      options.send_as_direct_message = SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect;

    await saveMessageInQueue({
      prefixKey,
      groupId: message.groupId,
      threadId: message.threadId,
      sendType: SystemConstant.SEND_TYPE.deleteMessage,
      content: message.sourceId,
      parentId: message.sourceId,
      options,
    });

    handleCloseConfirmDialog();
  };

  const handleShowDetail = () => {
    setIsDetailMessage(true);
    onClose();
  };

  const handlePinMessage = async () => {
    const newPinState = !isPin;
    const countPinMessage = await getInteractor(prefixKey).LocalGroupMessageService.count({
      type: SystemConstant.GROUP_MESSAGE_TYPE.pin,
      state: SystemConstant.STATE.active,
      group_id: message.groupId,
    });

    if (newPinState && countPinMessage >= MAX_GROUP_MESSAGE_PIN) {
      showAlert({ content: getCommonLang("TXT_OVER_MAX_PIN"), alertProps: { severity: "error" } });
    } else {
      dispatch(
        ConversationActions.pinMessage({
          sourceId: message.sourceId,
          type: SystemConstant.GROUP_MESSAGE_TYPE.pin,
          state: Boolean(newPinState) ? SystemConstant.PIN_TYPE.pin : SystemConstant.PIN_TYPE.unpin,
          groupId: message.groupId,
        }),
      );
    }

    setIsShowConfirmUnpin(false);
    onClose();
  };

  const handleShowConfirm = () => setIsShowConfirmUnpin(true);

  const handleUpdateThreadSetting = async () => {
    const newThreadSettingState = isTurnOffThreadNotice ? SystemConstant.STATE.active : SystemConstant.STATE.inactive;
    dispatch(
      NotificationSettingActions.updateThreadSetting(
        {
          threadId: message.sourceId,
          settingSubType: SystemConstant.SETTING_SUB_TYPE.NOTIFICATION_THREAD,
          created: Date.now(),
          state: newThreadSettingState,
          value: null,
        },
        prefixKey,
      ),
    );
    onClose();
  };

  const handleConfirmDeleteOnlyMe = async () => {
    await getInteractor(prefixKey).LocalApiCallService.update(
      { task: AppConstant.API_CALL_TASK.message },
      { task: AppConstant.API_CALL_TASK.delayMessage },
    );

    const options = {};
    if (message.sendAsDirectMessage)
      options.send_as_direct_message = SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect;

    const parentId = message.parentId || message.sourceId;
    await saveMessageInQueue({
      prefixKey,
      groupId: message.groupId,
      threadId: message.threadId,
      content: JSON.stringify({ source_id: parentId }),
      parentId: parentId,
      sendType: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
      options,
    });

    setIsShowConfirmDeleteOnlyMe(false);
    onClose();
  };

  const handleDeleteOnlyMe = () => {
    setIsShowConfirmDeleteOnlyMe(true);
    onClose();
  };

  const handleShowEditingHistory = () => {
    const originMessageSourceId = message.parentId || message.sourceId;
    if (originMessageSourceId) {
      showHistory(originMessageSourceId);
    } else {
      showAlert({ content: "Cannot find editing history for this message.", alertProps: { severity: "error" } });
    }
    onClose();
  };

  const isCallingMessage = SystemConstant.ARR_CALLING_TYPES.includes(message.sendType);
  const isEditable =
    !isCallingMessage &&
    isAllowEdit &&
    isMine &&
    !NOT_ALLOW_EDIT_TYPES.includes(message.sendType) &&
    !message.isDeleteAll &&
    !message.isDeleteOnlyMe &&
    !isSendErr;
  const isCopyable =
    !isCallingMessage &&
    !message.isDeleteOnlyMe &&
    !message.isDeleteAll &&
    ALLOW_COPY_TYPES.includes(message.sendType) &&
    !isSendErr;
  const isBot = groupDetail.isBot;
  const isShowPin =
    !isCallingMessage &&
    !isSendErr &&
    !isBot &&
    (groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal || groupDetail.isAdmin) &&
    !message.isDeleteAll &&
    !message.isDeleteOnlyMe &&
    message.sendType !== SystemConstant.SEND_TYPE.notFound;
  const isEnableDeleteAll =
    !isCallingMessage && !isSendErr && isMine && !message.isDeleteAll && !message.isDeleteOnlyMe;
  const isShowDeleteOnlyMe = !isSendErr;
  const isNotAllowReply =
    isCallingMessage ||
    Boolean(isThreadMode || isBot || message.isDisappear) ||
    (isCurrentDevice && false === Boolean(message.modified)) ||
    isSendErr;
  const isExistedMessage =
    false ===
    Boolean(message.isDeleteAll || message.isDeleteOnlyMe || message.sendType === SystemConstant.SEND_TYPE.notFound);
  const isMediaMessage = MEDIA_TYPES.includes(message.sendType) && isExistedMessage;

  const menuArray = [
    isNotAllowReply
      ? null
      : {
          onClick: handleReplyMessage,
          icon: <Reply />,
          content: getCommonLang(LangConstant.TXT_REPLY),
        },

    isExistedMessage && !isCallingMessage
      ? {
          onClick: handleShowDetail,
          icon: <Info />,
          content: getCommonLang("TXT_SEE_INFORMATION"),
        }
      : null,

    isEditable
      ? {
          onClick: onEditMessage,
          icon: <Edit />,
          content: getCommonLang(LangConstant.TXT_EDIT),
        }
      : null,

    isCopyable
      ? {
          onClick: handleCopyContent,
          icon: <ContentCopy />,
          content: getCommonLang(LangConstant.TXT_COPY),
        }
      : null,

    isMediaMessage
      ? {
          onClick: handleDownLoadFile,
          icon: <Download />,
          content: getCommonLang(LangConstant.TXT_DOWNLOAD),
        }
      : null,

    isEnableDeleteAll
      ? {
          onClick: handleShowConfirmDialog,
          icon: <DeleteAllIcon />,
          content: getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ALL_ACTION"),
        }
      : null,

    isShowPin
      ? {
          onClick: isPin ? handleShowConfirm : handlePinMessage,
          icon: isPin ? <UnpinIcon /> : <PushPinOutlined />,
          content: getCommonLang(isPin ? "TXT_UNPIN" : "TXT_PIN"),
        }
      : null,

    isThreadSetting
      ? {
          onClick: handleUpdateThreadSetting,
          icon: isTurnOffThreadNotice ? <NotificationsNone /> : <NotificationsOffOutlined />,
          content: getNSLang(
            LangConstant.NS_SETTING_NOTIFICATION,
            isTurnOffThreadNotice ? "TXT_TURN_ON_NOTICE_THREAD" : "TXT_TURN_OFF_NOTICE_THREAD",
          ),
        }
      : null,
    isShowDeleteOnlyMe
      ? {
          onClick: handleDeleteOnlyMe,
          icon: <DeleteIcon />,
          content: getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ONLY_ME_ACTION"),
        }
      : null,

    message.isEdited
      ? {
          onClick: handleShowEditingHistory,
          icon: <History />,
          content: getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_EDITING_HISTORY"),
        }
      : null,
  ].filter(item => Boolean(item && item.content));

  const handleGetLocalThreadSetting = async threadId => {
    const threadSetting = await getInteractor(prefixKey).LocalThreadSettingService.findOne({
      account_id: accountId,
      thread_id: threadId,
    });

    setIsTurnOffThreadNotice(threadSetting.state === SystemConstant.STATE.inactive);
  };

  useEffect(() => {
    if (message && menuAnchor) {
      setIsPin(message.isPin);
      if (isThreadSetting) handleGetLocalThreadSetting(message.sourceId);
    }
  }, [message, menuAnchor]);

  return (
    <>
      <ActionMenu contentArray={menuArray} onClose={onClose} anchorEl={menuAnchor} placement="top" />

      {isOpenConfirmDialog && (
        <ConfirmDialog
          open
          title={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ALL_ACTION")}
          content={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ALL_CONFIRM")}
          submitProps={{
            submitText: getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ALL_BUTTON"),
            onClick: handleDeleteMessage,
          }}
          cancelProps={{ onClick: handleCloseConfirmDialog }}
        />
      )}

      {isDetailMessage && (
        <MessageDetailDrawer message={message} open={isDetailMessage} onClose={() => setIsDetailMessage(false)} />
      )}

      {isShowConfirmUnpin && (
        <ConfirmDialog
          open
          title={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_CONFIRM_UNPIN_TITLE")}
          content={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_CONFIRM_UNPIN_SUBTITLE")}
          submitProps={{ submitText: getCommonLang("TXT_UNPIN"), onClick: handlePinMessage }}
          cancelProps={{
            onClick: () => {
              setIsShowConfirmUnpin(false);
              onClose();
            },
          }}
        />
      )}

      {isShowConfirmDeleteOnlyMe && (
        <ConfirmDialog
          open
          title={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ONLY_ME_ACTION")}
          content={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ONLY_ME_CONFIRM")}
          submitProps={{ submitText: getCommonLang("TXT_DELETE"), onClick: handleConfirmDeleteOnlyMe }}
          cancelProps={{
            onClick: () => {
              setIsShowConfirmDeleteOnlyMe(false);
              onClose();
            },
          }}
        />
      )}
    </>
  );
};

export default memo(ChatActionMenu);

const MEDIA_TYPES = [SystemConstant.SEND_TYPE.audio, SystemConstant.SEND_TYPE.image, SystemConstant.SEND_TYPE.video];
const NOT_ALLOW_EDIT_TYPES = [...MEDIA_TYPES, SystemConstant.SEND_TYPE.file, SystemConstant.SEND_TYPE.notFound];
const ALLOW_COPY_TYPES = [
  SystemConstant.SEND_TYPE.message,
  SystemConstant.SEND_TYPE.groupMessage,
  SystemConstant.SEND_TYPE.botMessage,
  SystemConstant.SEND_TYPE.quote,
  SystemConstant.SEND_TYPE.link,
];
